import { FieldWrapper } from "@progress/kendo-react-form";
import { TextBox, TextBoxProps } from "@progress/kendo-react-inputs";
import { Error, Hint } from "@progress/kendo-react-labels";
import * as React from "react";
import FormLabel from "./FormLabel";
import IconHint from "./IconHint";
import { useControlledField, type FieldValidator } from "./useControlledField";
import "./style.scss";

export interface FormTextBoxProps
  extends Omit<TextBoxProps, "name" | "value" | "onChange" | "valid"> {
  name: string;
  validator?: FieldValidator;
  label?: string;
  hint?: string;
  hintIcon?: boolean;
  optional?: boolean;
  showErrorOnPristine?: boolean;
  floatingLabel?: boolean;
  floatLabel?: string;
}

export const FormTextBox: React.FC<FormTextBoxProps> = ({
  name,
  validator,
  label,
  hint,
  hintIcon = false,
  optional,
  showErrorOnPristine = false,
  floatingLabel,
  floatLabel,
  type,
  disabled,
  placeholder,
  ...textBoxProps
}) => {
  const { field, fieldState } = useControlledField({ name, validator });
  const showError =
    !!fieldState.error && (showErrorOnPristine || fieldState.isTouched);
  const showHint = !!hint;
  const showHintText = showHint && !hintIcon;
  const showHintIcon = showHint && hintIcon;
  const inputId = name;

  return (
    <FieldWrapper>
      <FormLabel
        id={label ? `${inputId}_label` : ""}
        editorId={inputId}
        editorValid={!fieldState.error}
        editorDisabled={!!disabled}
        floating={floatingLabel}
        floatLabel={floatLabel}
        label={label}
        value={
          field.value != null && String(field.value).length > 0
            ? String(field.value)
            : undefined
        }
        placeholder={placeholder}
        optional={optional}
      >
        <div className={`k-form-field-wrap ${inputId}`}>
          <TextBox
            {...textBoxProps}
            id={inputId}
            name={name}
            type={type}
            disabled={disabled}
            placeholder={placeholder}
            autoComplete="off"
            style={{ paddingRight: 20 }}
            valid={!fieldState.error}
            value={(field.value as string | number | undefined) ?? ""}
            onChange={e => field.onChange(e.value)}
            onBlur={field.onBlur}
          />
          {showHintText && <Hint id={`${inputId}_hint`}>{hint}</Hint>}
          {showHintIcon && <IconHint hint={hint} />}
          {showError && (
            <Error id={`${inputId}_error`}>{fieldState.error?.message}</Error>
          )}
        </div>
      </FormLabel>
    </FieldWrapper>
  );
};

export default FormTextBox;
